import React, { useEffect, useState, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import FormCheck from 'react-bootstrap/FormCheck';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Inmate } from '@reliance/types/models/Inmate';
import { EnabledDisabled } from '@reliance/types/enums/EnabledDisabled';
import { useInmates } from '../hooks/Inmate';

interface IInmatePicker {
  onInmateSelected: (id: number | null) => void;
  initialInmateId?: number;
  inmateId?: number;
  showReleased?: boolean;
  placeholder?: string;
  disabled?: boolean;
  facilityId?: number;
}

const getInmateOptionLabel = (inmate: Inmate) => {
  const fullName = `${inmate.firstName} ${inmate.middleName || ''} ${inmate.lastName}`.trim();
  const status = inmate.status === EnabledDisabled.DISABLED ? ' (RELEASED)' : '';
  return `${fullName} (${inmate.account})${status}`;
};

const InmatePicker: React.FC<IInmatePicker> = (props) => {
  const [activeStatus, setActiveStatus] = useState<{ status?: string } | null>(null);
  const [inmate, setInmate] = useState<Inmate | null>(null);
  const [searchingReleased, setSearchingReleased] = useState<boolean>(false);

  const filters = useMemo(() => ({
    ...activeStatus,
    ...(props.facilityId && { facilityId: props.facilityId })
  }), [activeStatus, props.facilityId]);

  const inmates = useInmates(filters);
  const showReleased = props.showReleased !== undefined ? props.showReleased : true;

  // Handle inmateId changes from props
  useEffect(() => {
    if ((!props.inmateId || props.inmateId === null) && inmate && inmates.result) {
      setInmate(null);
    }
  }, [props.inmateId]);

  // Set initial inmate if initialInmateId is provided
  useEffect(() => {
    if (props.initialInmateId && inmates.result?.data) {
      const foundInmate = inmates.result.data.find((i) => i.id === props.initialInmateId);
      setInmate(foundInmate || null);
    }
  }, [props.initialInmateId, inmates.result]);

  // Update active status when searchingReleased changes
  useEffect(() => {
    setActiveStatus({ status: searchingReleased ? 'Any' : '' });
  }, [searchingReleased]);

  // Clear selected inmate if it's released and we're not searching released inmates
  useEffect(() => {
    if (!searchingReleased && inmate && inmate.status === EnabledDisabled.DISABLED) {
      setInmate(null);
    }
  }, [activeStatus, inmate, searchingReleased]);

  // Notify parent when inmate selection changes
  useEffect(() => {
    props.onInmateSelected(inmate ? inmate.id : null);
  }, [inmate]);

  const handleInmateSelect = (selected: Inmate[]) => {
    setInmate(selected.length > 0 ? selected[0] : null);
  };

  return (
    <div>
      <Form.Group>
        <Form.Label>Inmate</Form.Label>
        <Typeahead
          id="inmate-picker"
          labelKey={getInmateOptionLabel}
          options={inmates.result?.data || []}
          selected={inmate ? [inmate] : []}
          onChange={handleInmateSelect}
          placeholder={props.placeholder || 'Search inmates...'}
          disabled={props.disabled || inmates.loading}
          isLoading={inmates.loading}
          clearButton />

        {showReleased && (
          <FormCheck
            type="checkbox"
            label="Include Released Inmates"
            checked={searchingReleased}
            onChange={(e) => setSearchingReleased(e.target.checked)}
            className="mt-2" />
        )}
      </Form.Group>
    </div>
  );
};

export default InmatePicker;
